import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../../hooks/usePageTitle';
import {
  LogIn, LogOut, RefreshCw, Trash2, RotateCcw, Building2, User,
  CheckCircle, Clock, AlertCircle, ChevronDown, ChevronUp, Filter,
  BarChart3, List, Zap, Search, Star, ClipboardCheck, Mail, Check, Download
} from 'lucide-react';
import AdvisorsModule from './advisors/AdvisorsModule';
import { EmailPreviewModal } from './advisors/emails';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============== LOGIN ==============
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (res.ok) {
        const data = await res.json();
        onLogin(data.token);
      } else {
        setError('Credenciales incorrectas');
      }
    } catch {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d1b2a] flex items-center justify-center px-6">
      <form onSubmit={handleSubmit} className="w-full max-w-sm" data-testid="admin-login-form">
        <div className="text-center mb-10">
          <div className="w-12 h-12 bg-[#C58C40]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <LogIn size={20} className="text-[#C58C40]" />
          </div>
          <h1 className="text-white text-xl font-normal">Acceso Administración</h1>
        </div>
        <div className="space-y-4">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="admin-email"
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded text-sm text-white placeholder:text-white/30 outline-none focus:border-[#C58C40]/50" />
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Contraseña" data-testid="admin-password"
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded text-sm text-white placeholder:text-white/30 outline-none focus:border-[#C58C40]/50" />
          {error && <p className="text-red-400 text-xs">{error}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-btn"
            className="w-full bg-[#C58C40] text-white py-3 rounded text-sm tracking-wider uppercase hover:bg-[#b07b35] transition-colors disabled:opacity-50">
            {loading ? 'Accediendo...' : 'Acceder'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ============== HELPERS ==============
const STATUS_CFG = {
  forwarded: { label: 'Enviado', icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
  pending: { label: 'Pendiente', icon: Clock, color: 'text-amber-400', bg: 'bg-amber-400/10' },
  failed: { label: 'Fallido', icon: AlertCircle, color: 'text-red-400', bg: 'bg-red-400/10' },
};

function Badge({ status }) {
  const c = STATUS_CFG[status] || STATUS_CFG.pending;
  return <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium ${c.bg} ${c.color}`}><c.icon size={12} />{c.label}</span>;
}

function ScoreBar({ value, label }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-white/40 text-xs w-24 flex-shrink-0">{label}</span>
      <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div className="h-full bg-[#C58C40] rounded-full transition-all duration-500" style={{ width: `${value}%` }} />
      </div>
      <span className="text-white/60 text-xs w-10 text-right">{value}%</span>
    </div>
  );
}

function ChartBar({ label, value, max, color = 'bg-[#C58C40]' }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-white/50 text-xs w-32 flex-shrink-0 truncate">{label}</span>
      <div className="flex-1 h-4 bg-white/5 rounded-sm overflow-hidden">
        <div className={`h-full ${color} rounded-sm transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-white/70 text-sm font-medium w-8 text-right">{value}</span>
    </div>
  );
}

function KPI({ label, value, color = 'text-white' }) {
  return (
    <div className="bg-white/[0.02] border border-white/5 rounded p-4">
      <p className="text-white/30 text-xs mb-1">{label}</p>
      <p className={`${color} text-2xl font-light`}>{value}</p>
    </div>
  );
}

// ============== DASHBOARD TAB ==============
function DashboardTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [token]);

  if (loading) return <div className="text-white/30 text-sm py-16 text-center">Cargando dashboard...</div>;
  if (!data) return <div className="text-white/30 text-sm py-16 text-center">Error al cargar</div>;

  const t = data.totals;
  const maxSector = Math.max(...Object.values(data.empresa_sectors || { x: 0 }), 1);
  const maxArea = Math.max(...Object.values(data.advisor_areas || { x: 0 }), 1);
  const maxFact = Math.max(...Object.values(data.empresa_facturacion || { x: 0 }), 1);

  return (
    <div className="space-y-8">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPI label="Total" value={t.total} />
        <KPI label="Empresas" value={t.empresas} color="text-blue-400" />
        <KPI label="Advisors" value={t.advisors} color="text-purple-400" />
        <KPI label="Enviados" value={t.forwarded} color="text-emerald-400" />
        <KPI label="Pendientes" value={t.pending} color="text-amber-400" />
        <KPI label="Fallidos" value={t.failed} color="text-red-400" />
      </div>

      {/* Conversion */}
      <div className="bg-white/[0.02] border border-white/5 rounded p-6">
        <h3 className="text-white/70 text-sm font-medium mb-4">Tasa de conversión</h3>
        <div className="flex items-center gap-4">
          <div className="text-center flex-1">
            <p className="text-white text-2xl font-light">{t.total}</p>
            <p className="text-white/30 text-xs mt-1">Recibidos</p>
          </div>
          <div className="text-white/20">&rarr;</div>
          <div className="text-center flex-1">
            <p className="text-emerald-400 text-2xl font-light">{t.forwarded}</p>
            <p className="text-white/30 text-xs mt-1">Enviados</p>
          </div>
          <div className="text-white/20">&rarr;</div>
          <div className="text-center flex-1">
            <p className="text-[#C58C40] text-2xl font-light">{t.total > 0 ? Math.round((t.forwarded / t.total) * 100) : 0}%</p>
            <p className="text-white/30 text-xs mt-1">Tasa éxito</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sectors */}
        <div className="bg-white/[0.02] border border-white/5 rounded p-6">
          <h3 className="text-white/70 text-sm font-medium mb-4">Empresas por sector</h3>
          <div className="space-y-2.5">
            {Object.entries(data.empresa_sectors || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
              <ChartBar key={k} label={k} value={v} max={maxSector} />
            ))}
            {Object.keys(data.empresa_sectors || {}).length === 0 && <p className="text-white/20 text-xs">Sin datos</p>}
          </div>
        </div>

        {/* Facturacion */}
        <div className="bg-white/[0.02] border border-white/5 rounded p-6">
          <h3 className="text-white/70 text-sm font-medium mb-4">Empresas por facturación</h3>
          <div className="space-y-2.5">
            {Object.entries(data.empresa_facturacion || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
              <ChartBar key={k} label={k} value={v} max={maxFact} color="bg-blue-500" />
            ))}
            {Object.keys(data.empresa_facturacion || {}).length === 0 && <p className="text-white/20 text-xs">Sin datos</p>}
          </div>
        </div>

        {/* Advisor areas */}
        <div className="bg-white/[0.02] border border-white/5 rounded p-6">
          <h3 className="text-white/70 text-sm font-medium mb-4">Advisors por área</h3>
          <div className="space-y-2.5">
            {Object.entries(data.advisor_areas || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
              <ChartBar key={k} label={k} value={v} max={maxArea} color="bg-purple-500" />
            ))}
            {Object.keys(data.advisor_areas || {}).length === 0 && <p className="text-white/20 text-xs">Sin datos</p>}
          </div>
        </div>

        {/* Advisor levels */}
        <div className="bg-white/[0.02] border border-white/5 rounded p-6">
          <h3 className="text-white/70 text-sm font-medium mb-4">Advisors por nivel</h3>
          <div className="grid grid-cols-3 gap-4">
            {['R1', 'R2', 'R3'].map((n) => (
              <div key={n} className="text-center bg-white/[0.03] rounded p-4">
                <p className="text-[#C58C40] text-2xl font-light">{(data.advisor_niveles || {})[n] || 0}</p>
                <p className="text-white/40 text-xs mt-1">{n}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-2">
            {Object.entries(data.advisor_disponibilidad || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between text-xs">
                <span className="text-white/40">{k}</span>
                <span className="text-white/70">{v} advisors</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline */}
      {Object.keys(data.timeline || {}).length > 0 && (
        <div className="bg-white/[0.02] border border-white/5 rounded p-6">
          <h3 className="text-white/70 text-sm font-medium mb-4">Evolución temporal</h3>
          <div className="flex items-end gap-1 h-32">
            {Object.entries(data.timeline).map(([date, count]) => {
              const maxT = Math.max(...Object.values(data.timeline));
              const h = maxT > 0 ? (count / maxT) * 100 : 0;
              return (
                <div key={date} className="flex-1 flex flex-col items-center gap-1 group relative">
                  <div className="w-full bg-[#C58C40]/60 rounded-t-sm transition-all hover:bg-[#C58C40]" style={{ height: `${h}%`, minHeight: '4px' }} />
                  <span className="text-white/20 text-[9px] rotate-[-45deg] origin-top-left mt-1 hidden lg:block">{date.slice(5)}</span>
                  <div className="absolute -top-6 bg-white/10 text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity">{count}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

import { deadlineInfo, formatRemaining } from '../../lib/deadline';

function PlazoBadge({ createdAt, plazoHoras, stopped }) {
  if (stopped) return <span className="text-white/25 text-xs">—</span>;
  const info = deadlineInfo(createdAt, plazoHoras, stopped);
  if (!info) return <span className="text-white/20 text-xs">—</span>;
  return (
    <span className={`text-xs px-2 py-0.5 rounded ${info.urgency.bg} ${info.urgency.color}`} data-testid="plazo-badge">
      {info.urgency.label || formatRemaining(info.remainingHours)}
    </span>
  );
}

// ============== SUBMISSIONS TAB ==============
function SubmissionRow({ sub, token, onRefresh, plazoHoras, autoExpand }) {
  const [expanded, setExpanded] = useState(!!autoExpand);
  const [retrying, setRetrying] = useState(false);
  const [emailModal, setEmailModal] = useState(false);
  const rowRef = useRef(null);

  useEffect(() => {
    if (autoExpand && rowRef.current) rowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [autoExpand]);

  const handleRetry = async () => {
    setRetrying(true);
    await fetch(`${BACKEND_URL}/api/admin/retry-submission/${sub.submission_id}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    setRetrying(false);
    onRefresh();
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar este registro?')) return;
    await fetch(`${BACKEND_URL}/api/admin/submissions/${sub.submission_id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    onRefresh();
  };

  const handleMarcarRevisado = async (e) => {
    e.stopPropagation();
    await fetch(`${BACKEND_URL}/api/admin/submissions/${sub.submission_id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ revisado: !sub.revisado }),
    });
    onRefresh();
  };

  const p = sub.payload || {};
  const date = sub.created_at ? new Date(sub.created_at).toLocaleString('es-ES') : '—';

  return (
    <div ref={rowRef} className={`border rounded bg-white/[0.02] hover:bg-white/[0.04] transition-colors ${autoExpand ? 'border-[#C58C40]/50' : 'border-white/5'}`} data-testid={`submission-row-${sub.submission_id}`}>
      <div className="flex items-center gap-4 px-5 py-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${sub.form_type === 'empresa' ? 'bg-blue-500/10' : 'bg-purple-500/10'}`}>
          {sub.form_type === 'empresa' ? <Building2 size={14} className="text-blue-400" /> : <User size={14} className="text-purple-400" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium truncate">{p.nombre || '—'}</p>
          <p className="text-white/40 text-xs truncate">{p.email || p.empresa || '—'}</p>
        </div>
        <div className="hidden md:block text-white/30 text-xs w-36">{date}</div>
        {sub.form_type === 'empresa' && <PlazoBadge createdAt={sub.created_at} plazoHoras={plazoHoras} stopped={!!sub.revisado} />}
        <Badge status={sub.status} />
        <div className="flex items-center gap-2 ml-2">
          {sub.form_type === 'empresa' && (
            <button onClick={handleMarcarRevisado} data-testid={`submission-revisado-${sub.submission_id}`}
              title={sub.revisado ? 'Marcar como pendiente' : 'Marcar como revisado'}
              className={`px-2 py-1 rounded text-[11px] transition-colors ${sub.revisado ? 'bg-emerald-400/10 text-emerald-400' : 'bg-white/5 text-white/40 hover:text-white'}`}>
              {sub.revisado ? 'Revisado ✓' : 'Marcar revisado'}
            </button>
          )}
          {sub.status !== 'forwarded' && (
            <button onClick={(e) => { e.stopPropagation(); handleRetry(); }} disabled={retrying} title="Reintentar" className="p-1.5 rounded hover:bg-white/10 text-white/40 hover:text-[#C58C40] transition-colors">
              <RotateCcw size={14} className={retrying ? 'animate-spin' : ''} />
            </button>
          )}
          <button onClick={(e) => { e.stopPropagation(); handleDelete(); }} title="Eliminar" className="p-1.5 rounded hover:bg-white/10 text-white/40 hover:text-red-400 transition-colors">
            <Trash2 size={14} />
          </button>
          {expanded ? <ChevronUp size={14} className="text-white/20" /> : <ChevronDown size={14} className="text-white/20" />}
        </div>
      </div>
      {expanded && (
        <div className="px-5 pb-5 border-t border-white/5">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {Object.entries(p).map(([key, val]) => {
              let display = val;
              if (typeof val === 'boolean') display = val ? 'Sí' : 'No';
              else if (Array.isArray(val)) display = val.join(', ');
              else display = String(val || '—');
              return (
                <div key={key}>
                  <p className="text-white/30 text-xs uppercase tracking-wider mb-1">{key}</p>
                  <p className="text-white/80 text-sm break-words">{display}</p>
                </div>
              );
            })}
          </div>
          {sub.form_type === 'empresa' && (
            <div className="mt-4 border-t border-white/5 pt-4">
              <button onClick={() => setEmailModal(true)} data-testid="submission-email-E2"
                className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded px-3 py-1.5 text-xs text-white/70 hover:text-white">
                <Mail size={12} />Enviar Top 3 (E2)
                {(sub.emails_sent || {}).E2 && <Check size={12} className="text-emerald-400" />}
              </button>
              {(sub.emails_sent || {}).E2 && <span className="text-white/30 text-[10px] ml-2">Enviado el {(sub.emails_sent.E2 || '').slice(0, 16).replace('T', ' ')}</span>}
            </div>
          )}
        </div>
      )}
      {emailModal && (
        <EmailPreviewModal token={token} template="E2" nombre={p.nombre} referencia={sub.submission_id}
          entityType="empresa" entityId={sub.submission_id}
          onClose={() => setEmailModal(false)} onSent={onRefresh} />
      )}
    </div>
  );
}

function SubmissionsTab({ token, initialOpenId, onChanged }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [plazoHoras, setPlazoHoras] = useState(48);

  useEffect(() => {
    (async () => {
      const res = await fetch(`${BACKEND_URL}/api/admin/config`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setPlazoHoras((await res.json()).plazo_horas || 48);
    })();
  }, [token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterType) params.set('form_type', filterType);
    if (filterStatus) params.set('status', filterStatus);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/submissions?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setSubs(d.submissions); }
    } catch (e) { console.error(e); }
    setLoading(false);
    if (onChanged) onChanged();
  }, [token, filterType, filterStatus, onChanged]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = subs.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const p = s.payload || {};
    return (p.nombre || '').toLowerCase().includes(q) || (p.email || '').toLowerCase().includes(q) || (p.empresa || '').toLowerCase().includes(q) || (p.empresaActual || '').toLowerCase().includes(q) || (p.sector || '').toLowerCase().includes(q);
  });

  // Cola ordenada por urgencia: registros de empresa pendientes primero, por tiempo restante ascendente
  const sorted = [...filtered].sort((a, b) => {
    const aPending = a.form_type === 'empresa' && !a.revisado;
    const bPending = b.form_type === 'empresa' && !b.revisado;
    if (aPending && !bPending) return -1;
    if (!aPending && bPending) return 1;
    if (aPending && bPending) {
      const ra = deadlineInfo(a.created_at, plazoHoras, false)?.remainingHours ?? Infinity;
      const rb = deadlineInfo(b.created_at, plazoHoras, false)?.remainingHours ?? Infinity;
      return ra - rb;
    }
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const exportEmpresasXlsx = async () => {
    const res = await fetch(`${BACKEND_URL}/api/admin/export/empresas-xlsx`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'empresas.xlsx'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre, email, empresa, sector..."
            className="w-full pl-9 pr-4 py-2 bg-white/5 border border-white/10 rounded text-white text-xs outline-none focus:border-[#C58C40]/50" />
        </div>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="bg-white/5 border border-white/10 rounded px-3 py-2 text-white text-xs outline-none">
          <option value="">Todos</option><option value="empresa">Empresa</option><option value="advisor">Advisor</option>
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="bg-white/5 border border-white/10 rounded px-3 py-2 text-white text-xs outline-none">
          <option value="">Estados</option><option value="forwarded">Enviado</option><option value="pending">Pendiente</option><option value="failed">Fallido</option>
        </select>
        <button onClick={fetchData} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded px-3 py-2 text-white/60 hover:text-white text-xs transition-colors">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /><span>Actualizar</span>
        </button>
        <button onClick={exportEmpresasXlsx} data-testid="submissions-export-empresas-xlsx"
          className="flex items-center gap-2 bg-white/5 border border-white/10 rounded px-3 py-2 text-white/60 hover:text-white text-xs transition-colors">
          <Download size={12} /><span>Exportar Empresas (Excel)</span>
        </button>
      </div>
      <div className="space-y-2" data-testid="admin-submissions-list">
        {loading && sorted.length === 0 ? <p className="text-white/30 text-sm py-16 text-center">Cargando...</p>
          : sorted.length === 0 ? <p className="text-white/30 text-sm py-16 text-center">No hay registros</p>
          : sorted.map((s) => <SubmissionRow key={s.submission_id} sub={s} token={token} onRefresh={fetchData} plazoHoras={plazoHoras} autoExpand={s.submission_id === initialOpenId} />)}
      </div>
    </div>
  );
}

// ============== MATCHING TAB ==============
function MatchingTab({ token }) {
  const [empresas, setEmpresas] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [matches, setMatches] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/submissions?form_type=empresa`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) { const d = await res.json(); setEmpresas(d.submissions); }
      } catch (e) { console.error(e); }
      setLoadingList(false);
    })();
  }, [token]);

  const findMatches = async (id) => {
    setSelectedId(id);
    setLoading(true);
    setMatches(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/match/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setMatches(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  return (
    <div>
      <div className="mb-8">
        <h3 className="text-white/70 text-sm font-medium mb-3">Selecciona un mandato de empresa</h3>
        {loadingList ? <p className="text-white/30 text-xs">Cargando...</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {empresas.map((e) => {
              const p = e.payload || {};
              const active = selectedId === e.submission_id;
              return (
                <button key={e.submission_id} onClick={() => findMatches(e.submission_id)}
                  className={`text-left p-4 rounded border transition-all ${active ? 'border-[#C58C40] bg-[#C58C40]/5' : 'border-white/5 bg-white/[0.02] hover:bg-white/[0.04]'}`}>
                  <p className={`text-sm font-medium ${active ? 'text-[#C58C40]' : 'text-white'}`}>{p.empresa || p.nombre || '—'}</p>
                  <p className="text-white/40 text-xs mt-1">{p.sector || '—'} · {(p.areas || []).join(', ') || '—'}</p>
                </button>
              );
            })}
            {empresas.length === 0 && <p className="text-white/30 text-xs">No hay mandatos de empresa</p>}
          </div>
        )}
      </div>

      {loading && <p className="text-white/30 text-sm py-8 text-center">Calculando compatibilidad...</p>}

      {matches && (
        <div>
          {/* Company summary */}
          <div className="bg-blue-500/5 border border-blue-500/20 rounded p-5 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Building2 size={14} className="text-blue-400" />
              <span className="text-blue-400 text-xs uppercase tracking-wider">Mandato seleccionado</span>
            </div>
            <p className="text-white text-base font-medium">{matches.empresa.empresa}</p>
            <p className="text-white/50 text-xs mt-1">Sector: {matches.empresa.sector} · Áreas: {(matches.empresa.areas || []).join(', ')} · Modalidad: {matches.empresa.modalidad}</p>
          </div>

          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white/70 text-sm font-medium">
              {matches.matches.length} advisors analizados — ordenados por compatibilidad
            </h3>
          </div>

          {matches.matches.length === 0 ? (
            <p className="text-white/30 text-sm py-8 text-center">No hay advisors registrados para comparar</p>
          ) : (
            <div className="space-y-3">
              {matches.matches.map((m, i) => (
                <MatchCard key={m.submission_id} match={m} rank={i + 1} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MatchCard({ match: m, rank }) {
  const [expanded, setExpanded] = useState(false);
  const score = m.score.total;
  const scoreColor = score >= 70 ? 'text-emerald-400' : score >= 40 ? 'text-amber-400' : 'text-white/50';
  const scoreBg = score >= 70 ? 'bg-emerald-400/10 border-emerald-400/20' : score >= 40 ? 'bg-amber-400/10 border-amber-400/20' : 'bg-white/5 border-white/10';

  return (
    <div className={`border rounded transition-all ${rank <= 3 ? 'border-[#C58C40]/30 bg-[#C58C40]/[0.03]' : 'border-white/5 bg-white/[0.02]'}`}>
      <div className="flex items-center gap-4 px-5 py-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        {/* Rank */}
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-medium ${rank <= 3 ? 'bg-[#C58C40]/15 text-[#C58C40]' : 'bg-white/5 text-white/30'}`}>
          {rank <= 3 ? <Star size={14} className="fill-current" /> : rank}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium">{m.nombre}</p>
          <p className="text-white/40 text-xs">{m.cargo} · {m.empresa} · {m.nivel} · {m.experiencia}</p>
        </div>

        {/* Score */}
        <div className={`px-3 py-1.5 rounded border ${scoreBg}`}>
          <span className={`${scoreColor} text-lg font-medium`}>{score}%</span>
        </div>

        {expanded ? <ChevronUp size={14} className="text-white/20" /> : <ChevronDown size={14} className="text-white/20" />}
      </div>

      {expanded && (
        <div className="px-5 pb-5 border-t border-white/5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            {/* Score breakdown */}
            <div>
              <p className="text-white/40 text-xs uppercase tracking-wider mb-3">Desglose de compatibilidad</p>
              <div className="space-y-2">
                <ScoreBar value={m.score.breakdown.areas} label="Áreas" />
                <ScoreBar value={m.score.breakdown.sector} label="Sector" />
                <ScoreBar value={m.score.breakdown.modalidad} label="Modalidad" />
                <ScoreBar value={m.score.breakdown.nivel} label="Nivel" />
                <ScoreBar value={m.score.breakdown.disponibilidad} label="Disponibilidad" />
                <ScoreBar value={m.score.breakdown.idiomas} label="Idiomas" />
              </div>
            </div>
            {/* Profile details */}
            <div>
              <p className="text-white/40 text-xs uppercase tracking-wider mb-3">Perfil del advisor</p>
              <div className="space-y-2.5">
                <div><span className="text-white/30 text-xs">Email:</span> <span className="text-white/70 text-sm ml-2">{m.email}</span></div>
                <div><span className="text-white/30 text-xs">Áreas:</span> <span className="text-white/70 text-sm ml-2">{(m.areas || []).join(', ')}</span></div>
                <div><span className="text-white/30 text-xs">Sectores:</span> <span className="text-white/70 text-sm ml-2">{(m.sectores || []).join(', ')}</span></div>
                <div><span className="text-white/30 text-xs">Modalidad:</span> <span className="text-white/70 text-sm ml-2">{m.modalidad}</span></div>
                <div><span className="text-white/30 text-xs">Disponibilidad:</span> <span className="text-white/70 text-sm ml-2">{m.disponibilidad}</span></div>
                <div><span className="text-white/30 text-xs">Idiomas:</span> <span className="text-white/70 text-sm ml-2">{(m.idiomas || []).join(', ')}</span></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============== MAIN ADMIN ==============
const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'submissions', label: 'Registros', icon: List },
  { id: 'advisors', label: 'Advisors', icon: ClipboardCheck },
  { id: 'matching', label: 'Matching', icon: Zap },
];

function AdminDashboard({ token, onLogout }) {
  const params = new URLSearchParams(window.location.search);
  const [tab, setTab] = useState(params.get('tab') === 'submissions' || params.get('tab') === 'advisors' ? params.get('tab') : 'dashboard');
  const [pending, setPending] = useState({ advisors_pending: 0, empresas_pending: 0 });
  const initialCandidateId = params.get('candidate') || null;
  const initialOpenId = params.get('open') || null;

  const fetchPending = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/pending-summary`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setPending(await res.json());
    } catch (e) { /* silent */ }
  }, [token]);
  useEffect(() => {
    fetchPending();
    const id = setInterval(fetchPending, 60000);
    return () => clearInterval(id);
  }, [fetchPending]);

  // Si el token de sesión ya no es válido (p.ej. el backend se reinició), forzar logout
  // para que el usuario vea la pantalla de login en vez de un panel vacío.
  useEffect(() => {
    if (window.__conadvyFetchPatched) return;
    window.__conadvyFetchPatched = true;
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const res = await originalFetch(...args);
      const url = args[0]?.toString?.() || '';
      if (res.status === 401 && url.includes('/api/admin')) {
        sessionStorage.removeItem('admin_token');
        window.location.reload();
      }
      return res;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0d1b2a]">
      {/* Header */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-white text-lg font-normal">Conadvy</span>
            <span className="text-white/20">|</span>
            <span className="text-white/40 text-sm">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2" data-testid="pending-counter">
              <button onClick={() => setTab('advisors')} data-testid="pending-counter-advisors"
                className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded px-3 py-1.5 hover:bg-white/10 transition-colors">
                <span className="text-white/40 text-xs">Advisors pendientes</span>
                <span className={`text-sm font-medium ${pending.advisors_pending > 0 ? 'text-[#C58C40]' : 'text-white/40'}`}>{pending.advisors_pending}</span>
              </button>
              <button onClick={() => setTab('submissions')} data-testid="pending-counter-empresas"
                className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded px-3 py-1.5 hover:bg-white/10 transition-colors">
                <span className="text-white/40 text-xs">Empresas pendientes</span>
                <span className={`text-sm font-medium ${pending.empresas_pending > 0 ? 'text-[#C58C40]' : 'text-white/40'}`}>{pending.empresas_pending}</span>
              </button>
            </div>
            <button onClick={onLogout} data-testid="admin-logout" className="flex items-center gap-2 text-white/40 hover:text-white text-sm transition-colors">
              <LogOut size={14} /><span>Salir</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 flex gap-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-5 py-3 text-sm transition-colors border-b-2 ${
                tab === t.id ? 'text-[#C58C40] border-[#C58C40]' : 'text-white/40 border-transparent hover:text-white/70'
              }`}>
              <t.icon size={15} /><span>{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {tab === 'dashboard' && <DashboardTab token={token} />}
        {tab === 'submissions' && <SubmissionsTab token={token} initialOpenId={initialOpenId} onChanged={fetchPending} />}
        {tab === 'advisors' && <AdvisorsModule token={token} initialCandidateId={initialCandidateId} onChanged={fetchPending} />}
        {tab === 'matching' && <MatchingTab token={token} />}
      </div>
    </div>
  );
}

export default function AdminPage() {
  usePageTitle('Conadvy | Admin');
  const [token, setToken] = useState(() => sessionStorage.getItem('admin_token') || '');

  const handleLogin = (t) => { setToken(t); sessionStorage.setItem('admin_token', t); };
  const handleLogout = useCallback(() => { setToken(''); sessionStorage.removeItem('admin_token'); }, []);

  if (!token) return <LoginScreen onLogin={handleLogin} />;
  return <AdminDashboard token={token} onLogout={handleLogout} />;
}
